import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import { useListagemConfiguracaoContextOptional } from '@spark/ui-components/components/ListagemConfiguracao/ListagemConfiguracaoContext';
import styled from 'styled-components';
import { getDescargasColumns } from './tanStackColumns';
const DEFAULT_VISIBLE_COLUMNS = new Set([
  'localizacao',
  'acoes',
  'numeroCarga',
  'cargasCTE',
  'loja',
  'lojaRemetente',
  'cidade',
  'descricaoPosicaoAngellira',
  'numeroFrota',
  'frotasReboque',
  'quantidadePaletes',
  'descricaoTipoOperacao',
  'nomeResponsavel',
  'dataAgendamentoDescarga',
  'chegadaDescarga',
  'saidaDescarga',
  'tempoEsperaDescarga',
  'inicioContagem',
  'tempoForaJanela',
  'janelaAplicada',
  'tempoAtrasado',
  'clienteDistanciaAngellira',
  'clienteTempoPrevChegadaAngellira'
]);

const DescargasTable = ({
  data,
  loading,
  totalRegistros,
  paginationState,
  onPaginationChange,
  onRowClick,
  onDetalhes,
  onEditarRapida,
  onAuditoria,
  onLocalizacao,
  onAlterarStatus,
  onExcluirManual,
  loadingAcao,
  columnHeaderOverrides = {},
  visibleColumnIds: visibleColumnIdsProp,
  columnVisibilityOverride: columnVisibilityOverrideProp,
  columnConfigKey: columnConfigKeyProp,
  enableColumnConfig = true,
  staticTempoEspera = false,
  enableExportExcel = false,
  exportFileName = 'descargas-controle',
  onColumnLayoutChange,
  fullscreenContainerRef,
  isFullscreen = false
}) => {
  const listagemCtx = useListagemConfiguracaoContextOptional();
  const visibleColumnIds = listagemCtx?.visibleColumnIds ?? visibleColumnIdsProp;
  const columnVisibilityOverride = listagemCtx?.columnVisibilityOverride ?? columnVisibilityOverrideProp;
  const columnConfigKey = columnConfigKeyProp ?? listagemCtx?.columnConfigKey ?? 'descargas-controle-columns-v7';

  const handleColumnLayoutChangeMerged = useMemo(() => {
    if (!onColumnLayoutChange && !listagemCtx?.onColumnLayoutChange) {
      return undefined;
    }

    return (layout) => {
      listagemCtx?.onColumnLayoutChange?.(layout);
      onColumnLayoutChange?.(layout);
    };
  }, [listagemCtx, onColumnLayoutChange]);

  const tableConfigKey = useMemo(() => {
    if (!listagemCtx?.enabled) {
      return 'descargas-table-default';
    }

    return JSON.stringify({
      visibleColumnIds,
      columnVisibilityOverride
    });
  }, [columnVisibilityOverride, listagemCtx?.enabled, visibleColumnIds]);

  const columns = useMemo(
    () => {
      const baseColumns = getDescargasColumns(onAuditoria, loadingAcao, onLocalizacao, onAlterarStatus, {
        staticTempoEspera,
        onDetalhes,
        onEditarRapida,
        onExcluirManual
      })
        .map((column) => ({
          ...column,
          header: columnHeaderOverrides[column.id] || column.header
        }));

      if (!Array.isArray(visibleColumnIds) || visibleColumnIds.length === 0) {
        return baseColumns;
      }

      // visibleColumnIds define apenas a ORDEM (colunas da configuracao primeiro);
      // todas as colunas permanecem no array para o modal "Configurar colunas"
      // listar tambem as ocultas. A visibilidade fica por conta do columnVisibility
      // do initialState (columnVisibilityOverride da configuracao aplicada).
      const idsConfigurados = new Set(visibleColumnIds);
      const columnsById = new Map(baseColumns.map((column) => [column.id, column]));

      const configuradas = visibleColumnIds
        .map((columnId) => columnsById.get(columnId))
        .filter(Boolean);
      const restantes = baseColumns.filter((column) => !idsConfigurados.has(column.id));

      return [...configuradas, ...restantes];
    },
    [onAuditoria, onDetalhes, onEditarRapida, onLocalizacao, onAlterarStatus, onExcluirManual, loadingAcao, columnHeaderOverrides, visibleColumnIds, staticTempoEspera]
  );

  const tableInitialState = useMemo(() => {
    const configuracaoAtiva = Array.isArray(visibleColumnIds) && visibleColumnIds.length > 0;
    const idsConfigurados = configuracaoAtiva ? new Set(visibleColumnIds) : null;

    return {
      pageIndex: paginationState?.pageIndex || 0,
      pageSize: paginationState?.pageSize || 25,
      columnVisibility: columns.reduce((visibility, column) => {
        if (columnVisibilityOverride && Object.prototype.hasOwnProperty.call(columnVisibilityOverride, column.id)) {
          visibility[column.id] = columnVisibilityOverride[column.id];
          return visibility;
        }

        // Com configuracao do FilterPanel ativa, coluna fora da configuracao
        // (e sem entrada no override) inicia oculta em vez de cair no default.
        if (configuracaoAtiva) {
          if (!idsConfigurados.has(column.id) && column.enableHiding !== false) {
            visibility[column.id] = false;
          }
          return visibility;
        }

        if (!DEFAULT_VISIBLE_COLUMNS.has(column.id) && column.enableHiding !== false) {
          visibility[column.id] = false;
        }

        return visibility;
      }, {})
    };
  }, [columnVisibilityOverride, columns, paginationState?.pageIndex, paginationState?.pageSize, visibleColumnIds]);

  return (
    <Row>
      <Col md={12}>
        <FullscreenContainer ref={fullscreenContainerRef} $isFullscreen={isFullscreen}>
          <TanStackTableWrapper $isFullscreen={isFullscreen}>
            <TanStackTable
              key={tableConfigKey}
              columns={columns}
              data={data}
              onRowClick={onRowClick}
              isSortable={true}
              withPagination={true}
              manualPagination={true}
              totalItems={totalRegistros}
              onPaginationChange={onPaginationChange}
              controlledPagination={paginationState}
              pageSizeOptions={[10, 25, 50, 100, 150, 200]}
              initialState={tableInitialState}
              enableColumnConfig={enableColumnConfig}
              columnConfigKey={columnConfigKey}
              onColumnLayoutChange={handleColumnLayoutChangeMerged}
              enableExportExcel={enableExportExcel}
              exportFileName={exportFileName}
              enableRowColors={true}
              enableCopy={true}
              loading={!!loading}
              className="mt-3 descargas-tanstack-table"
            />
          </TanStackTableWrapper>
        </FullscreenContainer>
      </Col>
    </Row>
  );
};

export default DescargasTable;

const FullscreenContainer = styled.div`
  ${({ $isFullscreen }) => $isFullscreen && `
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    overflow: hidden;
    background-color: #ffffff;
    color: #121212;
    padding: 16px;
    display: flex;
    align-items: stretch;
    justify-content: center;

    .descargas-tanstack-table {
      width: 100%;
      height: 100%;
      margin-top: 0 !important;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .descargas-tanstack-table > div:first-child {
      display: none;
    }

    .descargas-tanstack-table > div:nth-child(2) {
      flex: 1 1 auto;
      min-height: 0;
      max-height: none !important;
      overflow: auto;
    }

    .descargas-tanstack-table > nav,
    .descargas-tanstack-table > div:last-child {
      display: none;
    }

    .descargas-tanstack-table thead th {
      font-size: 1.1rem !important;
      padding: 16px 12px !important;
    }

    .descargas-tanstack-table tbody td {
      font-size: 1.1rem !important;
      padding: 16px 12px !important;
    }
  `}
`;

const TanStackTableWrapper = styled.div`
  ${({ $isFullscreen }) => $isFullscreen && `
    width: min(100%, 1920px);
    height: calc(100vh - 32px);
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `}
`;
